import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Fade from "@mui/material/Fade";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import useQueryClient from "../../api/query";
import type { BackendFilters } from "../../utils/backendFilters";
import useDevMode from "../../utils/devmode";
import { FEATURE_FLAG_KEYS, useFeatureFlag } from "../../utils/featureFlags";
import { camelToSnakeCase, practiceSelectionKey } from "../../utils/utils";
import Loading from "../Loading";
import { BankConnectionsTab, ConnectBankButton } from "./BankConnectionsTab";
import { CREDENTIAL_TAB, CREDENTIAL_TYPE, typeFromCredential } from "./common";
import { Hero } from "./Hero";
import { NewCredentialsDialog } from "./NewCredentialsDialog/NewCredentialsDialog";
import { AddOpsCredentialButton } from "./SettingsDialog/OpsCredentialsDialog";
import { CredentialsTable } from "./Table/CredentialsTable";
import { OpsCredentialsTable } from "./Table/OpsCredentialsTable";

type CredentialArray = (
  | UserCredentialsMessage
  | Partial<UserCredentialsMessage>
)[];

interface CredentialsDashboardProps {
  practices: PracticeMessage[];
  selectedPractices: PracticeMessage[];
  supportedSources: SourceMessage[];
  supportedPMSes: PMSMessage[];
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}

export function CredentialsDashboard({
  practices,
  selectedPractices,
  supportedSources,
  supportedPMSes,
  updateSnackBar,
}: CredentialsDashboardProps) {
  const selectedPracticesKey = practiceSelectionKey(selectedPractices);
  const queryClient = useQueryClient();
  const devMode = useDevMode();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const [activeTab, setActiveTab] = useState<string>(() =>
    new URLSearchParams(window.location.search).get("tab") === "bank"
      ? CREDENTIAL_TAB.BANK
      : CREDENTIAL_TAB.PAYER,
  );
  const [payerCredentials, setPayerCredentials] = useState<
    CredentialArray | undefined
  >(undefined);
  const [pmsCredentials, setPmsCredentials] = useState<
    CredentialArray | undefined
  >(undefined);
  const [bankConnections, setBankConnections] = useState<
    PlaidConnectionMessage[] | undefined
  >(undefined);
  const [opsCredentials, setOpsCredentials] = useState<
    OpsCredentialsMessage[] | undefined
  >(undefined);
  const [opsPagination, setOpsPagination] = useState({
    page: 1,
    pageSize: 10,
    rowCount: 0,
  });
  const [opsLoading, setOpsLoading] = useState(false);
  const opsPageSizeRef = useRef(10);
  const opsSortRef = useRef<{
    field: string | null;
    direction: "asc" | "desc";
  }>({ field: "updatedAt", direction: "desc" });
  const opsFiltersRef = useRef<BackendFilters | null>(null);
  const opsAbortRef = useRef<AbortController | null>(null);
  const initialLoadDone = useRef(false);

  const { isEnabled: bankConnectionsEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.BANK_CONNECTIONS,
  );

  const supportedSourcesMap = supportedSources.reduce(
    (acc, source) => {
      acc[source.id] = source;
      return acc;
    },
    {} as Record<string, SourceMessage>,
  );

  const practiceFilters = useCallback((): Record<string, (string | null)[]> => {
    const practiceIds = selectedPractices.map((p) => p.wieldyId);
    return practiceIds.length > 0 ? { practice_id: practiceIds } : {};
  }, [selectedPractices]);

  const fetchPayerCredentials = useCallback(async () => {
    try {
      const response = await queryClientRef.current.getUserCredentials(
        1,
        practiceFilters(),
        undefined,
        undefined,
        CREDENTIAL_TAB.PAYER,
      );
      setPayerCredentials(response.data || []);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(`Failed to fetch payer credentials: ${error}`);
    }
  }, [practiceFilters]);

  const fetchPmsCredentials = useCallback(async () => {
    try {
      const response = await queryClientRef.current.getUserCredentials(
        1,
        practiceFilters(),
        undefined,
        undefined,
        CREDENTIAL_TAB.PMS,
      );
      setPmsCredentials(response.data || []);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(`Failed to fetch PMS credentials: ${error}`);
    }
  }, [practiceFilters]);

  const fetchBankConnections = useCallback(async () => {
    const practiceIds = selectedPractices.map((p) => p.wieldyId);
    if (practiceIds.length === 0) return;
    try {
      const data = await queryClientRef.current.getBankConnections(practiceIds);
      setBankConnections(data);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(`Failed to fetch bank connections: ${error}`);
    }
  }, [selectedPractices]);

  const fetchOpsCredentials = useCallback(
    async (
      signal?: AbortSignal,
      page = 1,
      pageSize = opsPageSizeRef.current,
    ) => {
      setOpsLoading(true);
      try {
        const sort = opsSortRef.current;
        const response = await queryClientRef.current.getUserCredentials(
          page,
          { ...(opsFiltersRef.current?.filters ?? {}), ...practiceFilters() },
          pageSize,
          signal,
          CREDENTIAL_TAB.OPS,
          {
            sortField: sort.field ? camelToSnakeCase(sort.field) : undefined,
            sortDirection: sort.direction,
            datagridFilters: opsFiltersRef.current?.datagridFilters,
            logicalOperator: opsFiltersRef.current?.logicalOperator,
          },
        );
        if (signal?.aborted) return;
        opsPageSizeRef.current = pageSize;
        setOpsCredentials(
          (response.data as OpsCredentialsMessage[] | undefined) || [],
        );
        setOpsPagination({ page, pageSize, rowCount: response.total });
      } catch (error) {
        if (
          error instanceof Error &&
          (error.name === "AbortError" || error.name === "CanceledError")
        ) {
          return;
        }
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error(`Failed to fetch ops credentials: ${error}`);
      } finally {
        setOpsLoading(false);
      }
    },
    [practiceFilters],
  );

  const handleOpsFilterChange = useCallback(
    (filters: BackendFilters) => {
      opsAbortRef.current?.abort();
      opsAbortRef.current = new AbortController();
      opsFiltersRef.current = filters;
      fetchOpsCredentials(opsAbortRef.current.signal, 1);
    },
    [fetchOpsCredentials],
  );

  const handleOpsSortChange = useCallback(
    (field: string | null, direction: "asc" | "desc") => {
      opsSortRef.current = { field, direction };
      fetchOpsCredentials(undefined, 1);
    },
    [fetchOpsCredentials],
  );

  const fetchAllCredentials = useCallback(async () => {
    await Promise.all([fetchPayerCredentials(), fetchPmsCredentials()]);
  }, [fetchPayerCredentials, fetchPmsCredentials]);

  const authorizeMfaEmail = async () => {
    try {
      const authEmailMessage = await queryClient.oauthEmail();
      window.location.href = authEmailMessage.redirectURL;
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(`Failed to oauth email: ${error}`);
    }
  };

  const setterForType = (
    credentialType: (typeof CREDENTIAL_TYPE)[keyof typeof CREDENTIAL_TYPE],
  ) =>
    credentialType === CREDENTIAL_TYPE.PAYER
      ? setPayerCredentials
      : setPmsCredentials;

  const addOptimisticCredential = (
    credential: Partial<UserCredentialsMessage>,
  ) => {
    const type = typeFromCredential(credential);
    const setter = setterForType(type);
    const temporaryClientId = `${credential.sourceId}-${credential.username}-${crypto?.randomUUID()}`;
    setter((prev) => [
      ...(prev ?? []),
      { ...credential, wieldyId: temporaryClientId },
    ]);
  };

  const afterAddCredential = async ({
    sourceId,
    practiceId,
    username,
    password,
  }: Partial<UserCredentialsMessage>) => {
    addOptimisticCredential({
      sourceId,
      practiceId,
      username,
      password,
    });
    await fetchAllCredentials();
  };

  const removeOptimisticCredential = (
    credentialId: UserCredentialsMessage["wieldyId"],
    credentialType: (typeof CREDENTIAL_TYPE)[keyof typeof CREDENTIAL_TYPE],
  ) => {
    const setter = setterForType(credentialType);
    setter((prev) => prev?.filter((c) => c.wieldyId !== credentialId));
  };

  const updateOptimisticCredential = (
    data: Partial<UserCredentialsMessage> & {
      wieldyId: UserCredentialsMessage["wieldyId"];
    },
    credentialType: (typeof CREDENTIAL_TYPE)[keyof typeof CREDENTIAL_TYPE],
  ) => {
    const setter = setterForType(credentialType);
    setter((prev) =>
      prev?.map((c) => (c.wieldyId === data.wieldyId ? { ...c, ...data } : c)),
    );
  };

  const unmaskCredential = async (
    wieldyId: string,
    attributeName: string,
    credentialType: (typeof CREDENTIAL_TYPE)[keyof typeof CREDENTIAL_TYPE],
  ) => {
    const response =
      credentialType === CREDENTIAL_TYPE.PMS
        ? await queryClient.unmaskPMSAttribute(wieldyId, attributeName)
        : await queryClient.unmaskSourceAttribute(wieldyId, attributeName);
    updateOptimisticCredential({ wieldyId, ...response }, credentialType);
    return response[attributeName as keyof Partial<UserCredentialsMessage>];
  };

  const makeGenerateRow =
    (
      credentials: CredentialArray | undefined,
      credentialType: (typeof CREDENTIAL_TYPE)[keyof typeof CREDENTIAL_TYPE],
    ) =>
    () => {
      const rows = credentials
        ?.map(
          ({
            wieldyId,
            practiceId,
            sourceId,
            sourceName,
            pmsDisplayName,
            username,
            password,
            notes,
            updatedAt,
            name,
            status,
            website,
            activationCode,
            serialNumber,
            customerNumber,
            machineIdentifier,
            userType,
            userId,
            mfaType,
            emailMfaSupported,
          }) => ({
            id: wieldyId,
            wieldyId,
            sourceId,
            practiceId,
            sourceName,
            pmsDisplayName,
            username,
            password,
            notes,
            updatedAt,
            practices,
            name,
            status,
            website,
            activationCode,
            serialNumber,
            customerNumber,
            machineIdentifier,
            userType,
            userId,
            mfaType,
            emailMfaSupported,
            fetchTotp: () =>
              wieldyId
                ? queryClient.getPayerCredentialTotp(wieldyId)
                : Promise.reject(new Error("Missing credential id")),
            fetchEmailMfa: () =>
              wieldyId
                ? queryClient.getPayerCredentialEmailMfa(wieldyId)
                : Promise.reject(new Error("Missing credential id")),
            removeUserCredential: () => {
              if (wieldyId) {
                removeOptimisticCredential(wieldyId, credentialType);
              }
            },
            updateUserCredential: (creds: Partial<UserCredentialsMessage>) => {
              if (wieldyId) {
                updateOptimisticCredential(
                  { wieldyId, ...creds },
                  credentialType,
                );
              }
            },
            unmaskValue: async (attributeName: string) => {
              if (
                wieldyId &&
                (sourceName || pmsDisplayName) &&
                (attributeName as keyof UnmaskAttributes) !== undefined
              ) {
                return unmaskCredential(
                  wieldyId,
                  attributeName,
                  credentialType,
                );
              }
              return "**********";
            },
            getUserCredentials: fetchAllCredentials,
          }),
        )
        .sort((a, b) => {
          const aDate = a.updatedAt ? new Date(a.updatedAt) : new Date();
          const bDate = b.updatedAt ? new Date(b.updatedAt) : new Date();
          return bDate.getTime() - aDate.getTime();
        });
      return rows || [];
    };

  const getCredentialNames = (credentials: CredentialArray | undefined) =>
    Array.from(
      new Set(
        credentials?.map(
          ({ sourceName, name, pmsDisplayName }) =>
            name || sourceName || pmsDisplayName || "-",
        ),
      ),
    ).sort();

  // Dev-mode-only tab; fall back to Payer if dev mode is off.
  useEffect(() => {
    if (activeTab === CREDENTIAL_TAB.OPS && !devMode) {
      setActiveTab(CREDENTIAL_TAB.PAYER);
    }
  }, [activeTab, devMode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping selectedPracticesKey would change when this runs
  useEffect(() => {
    if (practices.length === 0) return;
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchPayerCredentials();
      if (activeTab === CREDENTIAL_TAB.PMS) {
        fetchPmsCredentials();
      } else if (activeTab === CREDENTIAL_TAB.BANK) {
        fetchBankConnections();
      } else if (activeTab === CREDENTIAL_TAB.OPS) {
        fetchOpsCredentials();
      }
      return;
    }
    if (activeTab === CREDENTIAL_TAB.PAYER) {
      fetchPayerCredentials();
    } else if (activeTab === CREDENTIAL_TAB.PMS) {
      fetchPmsCredentials();
    } else if (activeTab === CREDENTIAL_TAB.BANK) {
      fetchBankConnections();
    } else if (activeTab === CREDENTIAL_TAB.OPS) {
      fetchOpsCredentials();
    }
  }, [
    practices.length,
    selectedPracticesKey,
    activeTab,
    fetchPayerCredentials,
    fetchPmsCredentials,
    fetchBankConnections,
    fetchOpsCredentials,
  ]);

  const credentialsNotLoaded =
    (payerCredentials === undefined && pmsCredentials === undefined) ||
    practices.length === 0;

  if (credentialsNotLoaded) {
    return (
      <Box>
        <Hero
          callToAction={
            <Button disabled variant="contained">
              Add Credential
            </Button>
          }
        />
        <Fade in timeout={500}>
          <Box sx={{ marginTop: "10rem" }}>
            <Loading />
          </Box>
        </Fade>
      </Box>
    );
  }

  const allCredentials = [
    ...(payerCredentials ?? []),
    ...(pmsCredentials ?? []),
  ];

  const tabLabel = (label: string, count: number, isActive: boolean) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {label}
      <Chip
        label={count}
        size="small"
        sx={{
          height: 20,
          minWidth: 20,
          fontSize: "0.75rem",
          fontWeight: 500,
          ...(isActive && {
            backgroundColor: "common.white",
            color: "primary.main",
          }),
        }}
      />
    </Box>
  );

  let heroCallToAction: React.ReactNode;
  if (activeTab === CREDENTIAL_TAB.BANK && bankConnectionsEnabled) {
    heroCallToAction = (
      <ConnectBankButton
        onSuccess={fetchBankConnections}
        updateSnackBar={updateSnackBar}
      />
    );
  } else if (activeTab === CREDENTIAL_TAB.OPS && devMode) {
    heroCallToAction = (
      <AddOpsCredentialButton
        practices={practices}
        afterSubmit={() => fetchOpsCredentials()}
        updateSnackBar={updateSnackBar}
      />
    );
  } else {
    heroCallToAction = (
      <NewCredentialsDialog
        credentialsStore={{ userCredentials: allCredentials }}
        practices={practices}
        supportedSources={supportedSources}
        supportedPMSes={supportedPMSes}
        afterFormSubmit={afterAddCredential}
        updateSnackBar={updateSnackBar}
        authorizeMfaEmail={authorizeMfaEmail}
      />
    );
  }

  return (
    <Box>
      <Hero callToAction={heroCallToAction} />
      <ToggleButtonGroup
        size="medium"
        color="primary"
        value={activeTab}
        exclusive
        onChange={(_, newValue) => {
          if (newValue) {
            setActiveTab(newValue);
          }
        }}
        aria-label="Credential Type Filters"
      >
        <ToggleButton value={CREDENTIAL_TAB.PAYER}>
          {tabLabel(
            "Payer Credentials",
            payerCredentials?.length ?? 0,
            activeTab === CREDENTIAL_TAB.PAYER,
          )}
        </ToggleButton>
        <ToggleButton value={CREDENTIAL_TAB.PMS}>
          {tabLabel(
            "PMS Credentials",
            pmsCredentials?.length ?? 0,
            activeTab === CREDENTIAL_TAB.PMS,
          )}
        </ToggleButton>
        {bankConnectionsEnabled && (
          <ToggleButton value={CREDENTIAL_TAB.BANK}>
            Bank Connections
          </ToggleButton>
        )}
        {devMode && (
          <ToggleButton value={CREDENTIAL_TAB.OPS}>
            {tabLabel(
              "Ops",
              opsPagination.rowCount,
              activeTab === CREDENTIAL_TAB.OPS,
            )}
          </ToggleButton>
        )}
      </ToggleButtonGroup>
      {activeTab === CREDENTIAL_TAB.PAYER && (
        <CredentialsTable
          updateSnackBar={updateSnackBar}
          supportedSourcesMap={supportedSourcesMap}
          practiceNames={practices.map(({ displayName }) => displayName)}
          selectedPractices={selectedPractices}
          showTypeColumn={false}
          credentialNames={getCredentialNames(payerCredentials)}
          generateRow={makeGenerateRow(payerCredentials, CREDENTIAL_TYPE.PAYER)}
          duplicateProps={{
            sources: supportedSources,
            pmses: supportedPMSes,
            practices,
            userCredentials: allCredentials,
            afterFormSubmit: afterAddCredential,
          }}
        />
      )}
      {activeTab === CREDENTIAL_TAB.PMS && (
        <CredentialsTable
          updateSnackBar={updateSnackBar}
          supportedSourcesMap={supportedSourcesMap}
          practiceNames={practices.map(({ displayName }) => displayName)}
          selectedPractices={selectedPractices}
          showTypeColumn={false}
          credentialNames={getCredentialNames(pmsCredentials)}
          generateRow={makeGenerateRow(pmsCredentials, CREDENTIAL_TYPE.PMS)}
        />
      )}
      {activeTab === CREDENTIAL_TAB.BANK && bankConnectionsEnabled && (
        <BankConnectionsTab
          connections={bankConnections ?? []}
          loading={bankConnections === undefined}
          onDisconnect={fetchBankConnections}
          onError={(msg) => updateSnackBar({ severity: "error", message: msg })}
          practices={practices}
        />
      )}
      {activeTab === CREDENTIAL_TAB.OPS && devMode && (
        <OpsCredentialsTable
          credentials={opsCredentials ?? []}
          practices={practices}
          loading={opsLoading}
          pagination={opsPagination}
          onPageChange={(page) => fetchOpsCredentials(undefined, page)}
          onPageSizeChange={(pageSize) =>
            fetchOpsCredentials(undefined, 1, pageSize)
          }
          onSortChange={handleOpsSortChange}
          onFilterChange={handleOpsFilterChange}
          onRefresh={() => fetchOpsCredentials(undefined, opsPagination.page)}
          updateSnackBar={updateSnackBar}
        />
      )}
    </Box>
  );
}
